import type { paths } from "@api/schema";

import { type DisplayBoardVisit, toBoardVisit } from "./clinicBoardView";
import type { Weight } from "./examRoomView";

/** Mirrors brian `modules/display/view_schemas.py` `TreatmentBoardView`; change both together. */
type DisplayViewOut =
  paths["/display/me/view"]["get"]["responses"][200]["content"]["application/json"];
type TreatmentBoardViewOut = Extract<
  DisplayViewOut,
  { kind: "treatment_board" }
>;
type PatientOut = TreatmentBoardViewOut["patients"][number];
type LineOut = PatientOut["lines"][number];
type StepOut = LineOut["components"][number];

export interface TreatmentStep {
  orderId: string;
  text: string;
  amount: string | null;
  state: StepOut["state"];
  partial: boolean;
  plannedAt: string;
  consented: boolean;
  progress: StepOut["progress"];
  progressAt: string | null;
  progressBy: string | null;
  byBrian: boolean;
}

export interface TreatmentLine extends TreatmentStep {
  components: TreatmentStep[];
}

export interface TreatmentPatient {
  visit: DisplayBoardVisit;
  reason: string | null;
  weight: Weight | null;
  staffName: string | null;
  returnRoom: string | null;
  lines: TreatmentLine[];
}

const amount = (step: StepOut): string | null =>
  step.quantity == null
    ? null
    : [String(step.quantity), step.unit].filter(Boolean).join(" ");

const toStep = (step: StepOut): TreatmentStep => ({
  orderId: step.order_uuid,
  text: step.text,
  amount: amount(step),
  state: step.state,
  partial: step.partial,
  plannedAt: step.planned_at,
  consented: step.consented,
  progress: step.progress,
  progressAt: step.progress_at,
  progressBy: step.progress_by,
  byBrian: step.by_brian,
});

const toPatient = (p: PatientOut): TreatmentPatient => ({
  visit: toBoardVisit(p.card),
  reason: p.reason,
  weight: p.weight && {
    value: String(p.weight.value),
    unit: p.weight.unit,
    observedAt: p.weight.observed_at,
  },
  staffName: p.staff_name,
  returnRoom: p.return_room,
  lines: p.lines.map((line) => ({
    ...toStep(line),
    components: line.components.map(toStep),
  })),
});

export const toTreatmentPatients = (
  view: TreatmentBoardViewOut,
): TreatmentPatient[] => view.patients.map(toPatient);
